import { supabase } from '@/config/supabase';
import type { RaidTarget } from '@/systems/RaidManager';
import type { RaidResult } from '@/models/types';

export class RaidService {
  async getTargets(playerEra: string, playerPower: number): Promise<RaidTarget[]> {
    // Matchmaking: same era, similar power (±50%), not self, limit 3
    const { data: session } = await supabase.auth.getSession();
    const userId = session.session?.user?.id;

    const minPower = Math.max(0, playerPower * 0.5);
    const maxPower = playerPower * 1.5 + 10; // +10 so new players still get targets

    const { data, error } = await supabase
      .from('players')
      .select('id, username, era, defense_power, production_rate')
      .eq('era', playerEra)
      .gte('defense_power', minPower)
      .lte('defense_power', maxPower)
      .neq('id', userId ?? '')
      .limit(10);

    if (error || !data) return [];

    // Shuffle and pick 3
    const shuffled = data.sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 3).map(p => ({
      id: p.id,
      username: p.username,
      era: p.era,
      defensePower: p.defense_power,
      productionRate: p.production_rate,
    }));
  }

  async executeRaid(defenderId: string, troopsInvested: number): Promise<{
    outcome: 'victory' | 'defeat';
    loot: { gold: number; food: number };
    defenderPower: number;
  } | null> {
    const { data: session } = await supabase.auth.getSession();
    const token = session.session?.access_token;
    if (!token) return null;

    const { data, error } = await supabase.functions.invoke('resolve-raid', {
      body: { defenderId, troopsInvested },
    });

    if (error || !data) return null;

    return {
      outcome: data.outcome,
      loot: data.loot,
      defenderPower: data.defenderPower,
    };
  }

  async getRaidHistory(): Promise<RaidResult[]> {
    const { data: session } = await supabase.auth.getSession();
    const userId = session.session?.user?.id;
    if (!userId) return [];

    const { data, error } = await supabase
      .from('raids')
      .select('*')
      .or(`attacker_id.eq.${userId},defender_id.eq.${userId}`)
      .order('created_at', { ascending: false })
      .limit(20);

    if (error || !data) return [];

    return data.map(r => ({
      id: r.id,
      attackerId: r.attacker_id,
      defenderId: r.defender_id,
      troopsInvested: r.troops_invested,
      outcome: r.outcome,
      loot: r.loot,
      createdAt: r.created_at,
    }));
  }
}
